import { DefaultValues } from 'react-hook-form';
import { array, boolean, date, number, object, ObjectSchema, string } from 'yup';

import {
  CreateOrUpdateIdeaInput,
  CreateOrUpdateWhitePaperInput,
  IdeaSubmissionDraftFragment,
  WhitePaperSubmissionDraftFragment
} from '@interfaces/graphql-typings';

const {
  catalystNames,
  entityMentionLimit,
  investmentSituations,
  targetAllocation,
  whitePaperValidations
} = require('buyside/util/constantsFromRails.json');

// Idea Submission

export type IdeaFormInput = Omit<CreateOrUpdateIdeaInput, 'mentionedEntities' | 'attachments'> & {
  mentionedEntities: { label: string; value: string }[];
  attachments: (CreateOrUpdateIdeaInput['attachments'][number] & {
    fromUpdate: boolean;
    url: string;
  })[];
};

export const IDEA_SUBMISSION_TIMEFRAMES = [
  'less_than_3_months',
  '3_months_to_6_months',
  '6_months_to_1_year',
  '1_year_to_2_years',
  '2_years_to_5_years',
  '5_years_to_7_years',
  '7_years_to_10_years',
  '10_years_to_12_years'
];

const userCreditDataSchema = object({
  amountOutstanding: number()
    .nullable()
    .required('This field is required.'),
  currentYield: number()
    .nullable()
    .notRequired(),
  convertible: boolean().required(),
  couponFrequency: string().when('couponType', {
    is: value => Boolean(value),
    then: string()
      .nullable()
      .required('This field is required.'),
    otherwise: string()
      .nullable()
      .notRequired()
  }),
  couponRate: number().when('couponType', {
    is: value => Boolean(value),
    then: number()
      .nullable()
      .required('This field is required.'),
    otherwise: number()
      .nullable()
      .notRequired()
  }),
  couponType: string()
    .nullable()
    .notRequired(),
  embeddedOption: string().required('This field is required.'),
  fitchCreditRating: string().required('This field is required.'),
  guaranteed: boolean().required(),
  issueSize: number()
    .nullable()
    .required('This field is required.'),
  lien: string().when('securityClass', {
    is: 'secured',
    then: string()
      .nullable()
      .required('This field is required.'),
    otherwise: string()
      .nullable()
      .notRequired()
  }),
  maturityDate: date()
    .typeError('Please enter a valid date.')
    .required('This field is required.'),
  moodyCreditRating: string().required('This field is required.'),
  securedBy: string().when('securityClass', {
    is: 'secured',
    then: string()
      .nullable()
      .required('This field is required.'),
    otherwise: string()
      .nullable()
      .notRequired()
  }),
  securityClass: string().required('This field is required.'),
  seniority: string().required('This field is required.'),
  standardCreditRating: string().required('This field is required.')
});

const userDefinedAssetSchema = object({
  assetClassId: string().required('Please select an asset class'),
  currencyId: string().required('Please select a currency'),
  entityName: string().required('Please enter an entity name'),
  recentPrice: number()
    .nullable()
    .required('Please enter a recent price'),
  symbol: string().required('Please enter a symbol'),
  userCreditData: userCreditDataSchema.nullable().notRequired()
});

export const generateValidationSchema = ({
  currentStepName,
  isPairTrade
}: {
  currentStepName: string;
  isPairTrade: boolean;
}): ObjectSchema => {
  let schema = {};

  if (currentStepName === 'position_and_details')
    schema = {
      positions: array().of(
        object({
          positionId: string().nullable(),
          assetId: string().when('userDefinedAsset', {
            is: null,
            then: string().required('Please search and select a security'),
            otherwise: string().notRequired()
          }),
          direction: string().oneOf(['long', 'short'], 'Please select a direction'),
          priceTarget: isPairTrade
            ? number().nullable()
            : number()
                .nullable()
                .required('Please provide a target price')
                .positive('Please provide a positive target price'),
          userDefinedAsset: userDefinedAssetSchema.nullable().notRequired()
        })
      ),
      pairRatio: isPairTrade
        ? array().of(
            number()
              .nullable()
              .required('Please provide a ratio')
              .positive('Please provide a positive ratio')
          )
        : array()
            .of(number().nullable())
            .nullable(),
      returnTarget: isPairTrade
        ? number()
            .nullable()
            .required('Please provide a return target')
            .positive('Please provide a positive return target')
        : number().nullable(),
      portfolioPercentage: string().oneOf(targetAllocation, 'Please select your target allocation'),
      timeframe: string().oneOf(IDEA_SUBMISSION_TIMEFRAMES, 'Please select a timeframe'),
      situation: string().oneOf(investmentSituations, 'Please select a situation'),
      catalystNames: array().of(string().oneOf(catalystNames))
    };

  if (currentStepName === 'writeup_and_attachments')
    schema = {
      elevatorPitch: string()
        .required('Please provide an elevator pitch')
        .max(190, 'Your elevator pitch must be less than 190 characters'),
      attachments: array().of(
        object({
          researchType: string().required()
        })
      ),
      thesis: string().when('attachments', {
        is: (attachments: IdeaFormInput['attachments']) =>
          attachments.some(attachment => attachment.researchType === 'Thesis'),
        then: string().notRequired(),
        otherwise: string().required('Please provide a thesis')
      }),
      tagList: array()
        .of(string().required())
        .test('length', 'Tags must be less than 50 characters', (value: string[]) => {
          return value.every(tag => tag.length <= 50);
        }),
      mentionedEntities: array()
        .of(
          object({
            label: string().required(),
            value: string().required()
          })
        )
        .max(entityMentionLimit, `You can only tag up to ${entityMentionLimit} tickers`)
    };

  if (currentStepName === 'review_and_submit')
    schema = {
      contests: array().of(string().nullable()),
      positionDisclaimerIndividual: boolean().required(),
      anonymous: boolean().required(),
      employerId: string().nullable(),
      positionDisclaimerFund: boolean().required(),
      fundProfileDisplay: boolean().required(),
      publicInformationDisclaimer: boolean().required(),
      employerApprovalDisclaimer: boolean().required(),
      noPriorAgreementDisclaimer: boolean().required()
    };

  return object(schema);
};

export const userCreditDataDefaultValues = {
  amountOutstanding: null,
  convertible: false,
  couponFrequency: null,
  couponRate: null,
  couponType: null,
  currentYield: null,
  embeddedOption: '',
  fitchCreditRating: '',
  guaranteed: false,
  issueSize: null,
  lien: null,
  maturityDate: '',
  moodyCreditRating: '',
  securedBy: null,
  securityClass: '',
  seniority: '',
  standardCreditRating: ''
};

export const generateDefaultValues = (idea?: IdeaSubmissionDraftFragment): IdeaFormInput => {
  const {
    id: ideaId,
    anonymous: ideaAnonymous,
    associatedEmployer: ideaAssociatedEmployer,
    catalystNames: ideaCatalystNames,
    contestIds: contests,
    elevatorPitch: ideaElevatorPitch,
    employerApprovalDisclaimer: ideaEmployerApprovalDisclaimer,
    featuredOnAFundProfile: ideaFundProfileDisplay,
    ideaAttachments,
    mentionedEntities: ideaMentionedEntities,
    noPriorAgreementDisclaimer: ideaNoPriorAgreementDisclaimer,
    pairRatio: ideaPairRatio,
    positionDisclaimerFund: ideaPositionDisclaimerFund,
    positionDisclaimerIndividual: ideaPositionDisclaimerIndividual,
    positions: ideaPositions,
    publicInformationDisclaimer: ideaPublicInformationDisclaimer,
    returnTarget: ideaReturnTarget,
    situation: ideaSituation,
    tagList: ideaTagList,
    targetAllocation: ideaTargetAllocation,
    thesis: ideaThesis,
    timeframe: ideaTimeframe
  } = idea || {};

  return {
    ideaId: ideaId || null,
    anonymous: Boolean(ideaAnonymous),
    catalystNames: ideaCatalystNames || [],
    contests: contests || [],
    currentStep: '',
    elevatorPitch: ideaElevatorPitch || '',
    employerApprovalDisclaimer: Boolean(ideaEmployerApprovalDisclaimer),
    employerId: ideaAssociatedEmployer?.id || '',
    attachments:
      ideaAttachments?.map(({ id, attachmentType, filename, fromUpdate, researchType, url }) => ({
        id,
        attachmentType,
        file: null,
        filename: filename || '',
        fromUpdate: Boolean(fromUpdate),
        researchType,
        url: url || ''
      })) || [],
    isSubmission: false,
    mentionedEntities: ideaMentionedEntities
      ? ideaMentionedEntities.map(({ id, name, primarySymbol }) => ({
          label: `${primarySymbol} - ${name}`,
          value: id
        }))
      : [],
    noPriorAgreementDisclaimer: Boolean(ideaNoPriorAgreementDisclaimer),
    pairRatio: ideaPairRatio?.length ? ideaPairRatio : null,
    portfolioPercentage: ideaTargetAllocation || '',
    positionDisclaimerFund: Boolean(ideaPositionDisclaimerFund),
    positionDisclaimerIndividual: Boolean(ideaPositionDisclaimerIndividual),
    positions: ideaPositions?.length
      ? ideaPositions.map(position => ({
          positionId: position.id,
          assetId: position.asset?.id || '',
          direction: position.direction,
          priceTarget: position.priceTarget || null,
          userDefinedAsset: null
        }))
      : [
          {
            positionId: null,
            assetId: '',
            direction: '',
            priceTarget: null,
            userDefinedAsset: null
          }
        ],
    fundProfileDisplay: Boolean(ideaFundProfileDisplay),
    publicInformationDisclaimer: Boolean(ideaPublicInformationDisclaimer),
    returnTarget: ideaReturnTarget || null,
    situation: ideaSituation || '',
    tagList: ideaTagList || [],
    thesis: ideaThesis || '',
    timeframe: ideaTimeframe || ''
  };
};

export const determineCompletedSteps = (idea?: DefaultValues<IdeaFormInput>): string[] => {
  const completedSteps: string[] = [];

  const {
    pairRatio,
    positions,
    returnTarget,
    portfolioPercentage,
    timeframe,
    situation,
    elevatorPitch,
    thesis,
    publicInformationDisclaimer,
    employerApprovalDisclaimer,
    noPriorAgreementDisclaimer
  } = idea || {};

  const isPairTrade = positions?.length === 2;

  if (!positions?.length || positions.some(position => !position?.assetId || !position.direction))
    return completedSteps;

  if (
    Boolean(isPairTrade && pairRatio?.some(ratio => !ratio)) ||
    Boolean(isPairTrade && !returnTarget) ||
    Boolean(!portfolioPercentage || !timeframe || !situation)
  )
    return completedSteps;

  completedSteps.push('position_and_details');

  if (!elevatorPitch?.length || !thesis?.length) return completedSteps;

  completedSteps.push('writeup_and_attachments');

  if (!publicInformationDisclaimer || !employerApprovalDisclaimer || !noPriorAgreementDisclaimer)
    return completedSteps;

  completedSteps.push('review_and_submit');

  return completedSteps;
};

export const determineIsSubunitCurrency = (currencyCode: string): boolean => {
  return currencyCode === 'GBp' || currencyCode === 'ILs';
};

export const determineParentCurrencyCode = (currencyCode: string): string => {
  if (currencyCode === 'GBp') return 'GBP';
  if (currencyCode === 'ILs') return 'ILS';

  return currencyCode;
};

export const determineParentCurrencyName = (currencyName: string): string => {
  if (currencyName === 'British Pence') return 'Great British Pound';
  if (currencyName === 'Israeli Agora') return 'Israeli Shekel';

  return currencyName;
};

// White Paper Submission

export type WhitePaperFormInput = Omit<
  CreateOrUpdateWhitePaperInput,
  'mentionedEntities' | 'attachments'
> & {
  mentionedEntities: { label: string; value: string }[];
  publicInformationDisclaimer: boolean;
  employerApprovalDisclaimer: boolean;
  noPriorAgreementDisclaimer: boolean;
  attachments: (CreateOrUpdateWhitePaperInput['attachments'][number] & {
    url: string;
  })[];
};

export const generateWhitePaperValidationSchema = ({
  currentStepName
}: {
  currentStepName: string;
}): ObjectSchema => {
  let schema = {};

  if (currentStepName === 'title_and_categories')
    schema = {
      title: string()
        .required('Title is required')
        .min(
          whitePaperValidations.titleMinChars,
          `Title must be at least ${whitePaperValidations.titleMinChars} characters long`
        )
        .max(
          whitePaperValidations.titleMaxChars,
          `Title must be shorter than ${whitePaperValidations.titleMaxChars} characters long`
        ),
      sectors: array().of(number()),
      geographies: array().of(number()),
      globalMacros: array().of(number()),
      esgs: array().of(number())
    };

  const schemaWithCategoryValidation = object(schema).test(
    'total-categories-limit',
    'Please select no more than 5 categories',
    function(values) {
      if (currentStepName !== 'title_and_categories') return true;

      const sectorsCount = values?.sectors?.length || 0;
      const geographiesCount = values?.geographies?.length || 0;
      const globalMacrosCount = values?.globalMacros?.length || 0;
      const esgsCount = values?.esgs?.length || 0;

      const totalCount = sectorsCount + geographiesCount + globalMacrosCount + esgsCount;

      if (totalCount === 0) {
        return this.createError({
          path: 'totalCategories',
          message: 'Please select at least one category'
        });
      }

      if (totalCount > 5) {
        return this.createError({
          path: 'totalCategories',
          message: 'Please select no more than 5 categories'
        });
      }

      return true;
    }
  );

  if (currentStepName === 'writeup_and_attachments')
    schema = {
      attachments: array().of(
        object({
          researchType: string().required()
        })
      ),
      elevatorPitch: string()
        .required('Elevator pitch is required')
        .max(
          whitePaperValidations.elevatorPitchMaxChars,
          `Elevator Pitch must be no longer than ${whitePaperValidations.elevatorPitchMaxChars} characters.`
        ),
      thesis: string().when('attachments', {
        is: (attachments: WhitePaperFormInput['attachments']) =>
          attachments.some(attachment => attachment.attachmentType === 'thesis'),
        then: string().notRequired(),
        otherwise: string().required('Please provide a thesis')
      }),
      mentionedEntities: array()
        .of(
          object({
            label: string().required(),
            value: string().required()
          })
        )
        .max(entityMentionLimit, `You can only tag up to ${entityMentionLimit} tickers`),
      tagList: array()
        .of(string().required())
        .test('length', 'Tags must be less than 50 characters', (value: string[]) => {
          return value.every(tag => tag.length <= 50);
        })
    };

  if (currentStepName === 'review_and_submit')
    schema = {
      positionDisclaimerIndividual: boolean().required(),
      employerId: string().nullable(),
      positionDisclaimerFund: boolean().required(),
      fundProfileDisplay: boolean().required(),
      publicInformationDisclaimer: boolean().required(),
      employerApprovalDisclaimer: boolean().required(),
      noPriorAgreementDisclaimer: boolean().required()
    };

  return currentStepName === 'title_and_categories' ? schemaWithCategoryValidation : object(schema);
};

export const generateWhitepaperDefaultValues = (
  whitePaper?: WhitePaperSubmissionDraftFragment
): WhitePaperFormInput => {
  const {
    elevatorPitch,
    esgs,
    geographies,
    globalMacros,
    id: whitePaperId,
    sectors,
    thesis,
    title,
    mentionedEntities,
    tagList,
    positionDisclaimerIndividual,
    positionDisclaimerFund,
    featuredOnAFundProfile,
    status,
    whitePaperAttachments
  } = whitePaper || {};

  return {
    whitePaperId,
    attachments:
      whitePaperAttachments?.map(({ id, attachmentType, filename, researchType, url }) => ({
        id,
        attachmentType,
        file: null,
        filename: filename || '',
        researchType,
        url: url || ''
      })) || [],
    currentStep: '',
    esgs: esgs ? esgs.map(esg => esg.id) : [],
    geographies: geographies ? geographies.map(geography => geography.id) : [],
    globalMacros: globalMacros ? globalMacros.map(globalMacro => globalMacro.id) : [],
    sectors: sectors ? sectors.map(sector => sector.id) : [],
    title: title || '',
    elevatorPitch: elevatorPitch || '',
    thesis: thesis || '',
    mentionedEntities:
      mentionedEntities?.map(entity => ({
        label: entity.name || '',
        value: entity.id
      })) || [],
    tagList: tagList || [],
    positionDisclaimerIndividual: Boolean(positionDisclaimerIndividual),
    employerId: whitePaper?.associatedEmployer?.id || '',
    positionDisclaimerFund: Boolean(positionDisclaimerFund),
    fundProfileDisplay: Boolean(featuredOnAFundProfile),
    publicInformationDisclaimer: status === 'draft' ? false : true,
    employerApprovalDisclaimer: status === 'draft' ? false : true,
    noPriorAgreementDisclaimer: status === 'draft' ? false : true,
    isSubmission: false
  };
};

export const determineWhitePaperCompletedSteps = (
  whitePaper?: DefaultValues<WhitePaperFormInput>
): string[] => {
  const completedSteps: string[] = [];

  const {
    elevatorPitch,
    thesis,
    title,
    sectors,
    geographies,
    globalMacros,
    esgs,
    publicInformationDisclaimer,
    employerApprovalDisclaimer,
    noPriorAgreementDisclaimer
  } = whitePaper || {};

  if (title && (sectors?.length || geographies?.length || globalMacros?.length || esgs?.length)) {
    completedSteps.push('title_and_categories');
  }

  if (elevatorPitch && thesis) {
    completedSteps.push('writeup_and_attachments');
  }

  if (publicInformationDisclaimer && employerApprovalDisclaimer && noPriorAgreementDisclaimer) {
    completedSteps.push('review_and_submit');
  }

  return completedSteps;
};

// Idea & White Paper Submission

export const DISCLAIMERS = {
  REQUIRED: {
    publicInformationDisclaimer:
      'This investment write-up in no way incorporates any non-public information and/or unsubstantiated rumors or misrepresentations and does not involve any act, practice, or course of business which operates or would operate as a fraud or deceit upon any person in connection with an investment.',
    employerApprovalDisclaimer:
      'I have obtained all necessary approvals from my employer to submit this investment write-up to the SumZero Research Database and it complies with all applicable policies of my employer.',
    noPriorAgreementDisclaimer:
      'This submission does not violate any agreements to which I may be subject (including, without limitation, any confidentiality agreements), insider trading regulations, SEC regulations, and/or other applicable laws, rules and regulations.'
  },
  OPTIONAL: {
    positionDisclaimerIndividual:
      'I have a position (long or short) in this security and may trade in and out of this position without informing the SumZero community.',
    positionDisclaimerFund:
      'My fund has a position (long or short) in this security and may trade in and out of this position without informing the SumZero community.'
  }
};
